
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Button,
  InputField,
  TextAreaField,
  CircularLoader,
  IconCheckmark24,
  IconClock24,
  IconInfo24,
  Card,
  NoticeBox,
} from '@dhis2/ui';
import { useDataEngine } from '@dhis2/app-runtime';
import { useAuth } from './../auth/AuthContext';
import { getAuthHeader } from '../../utils/auth';
import { POSITION_ATTRIBUTES } from '../staff/api/constants';
import { DATA_ELEMENTS } from './api/appraisalConstants';
import { OBJECTIVE_APPRAISAL_DE } from './api/appraisalConstants';
import { ChevronRight, TrendingUp, Star, MessageSquare, CheckCircle, Clock, RotateCcw, Lock, Users, AlertCircle } from 'lucide-react';
import './AppraisalForm.css';

// Status codes
const APPRAISAL_STATUS_CODES = {
  DRAFT: 'draft',
  SUBMITTED: 'submitted',
  REVIEWED: 'reviewed',
  FINALIZED: 'finalized',
  RETURNED: 'returned',  
};

// Objective Data Elements (from your PerformanceList)
const OBJECTIVE_DATA_ELEMENTS = {
  NAME: 'EuN5ztFnXsQ',
  DESCRIPTION: 'nWqW5BrcgP1',
  START_DATE: 'eDcVy661j9e',
  COMPLETION_DATE: 'J0rjxrDKn1l',
  APPROVAL_STATUS: 'DzQorireRBf',
};

// ─── Progress Bar ────────────────────────────────────────────────────────────
const ProgressBar = ({ value = 0 }) => {
  const fillClass =
    value >= 80 ? 'apr-progress-fill--high'
    : value >= 50 ? 'apr-progress-fill--mid'
    : 'apr-progress-fill--low';
  return (
    <div className="apr-progress-wrap">
      <div className="apr-progress-track">
        <div
          className={`apr-progress-fill ${fillClass}`}
          style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
        />
      </div>
      <span className="apr-progress-pct">{value}%</span>
    </div>
  );
};

// ─── Status Chip ─────────────────────────────────────────────────────────────
const StatusChip = ({ status }) => {
  const map = {
    approved:   { label: 'Approved',  cls: 'apr-chip--approved',  icon: <CheckCircle size={11} /> },
    returned:   { label: 'Returned',  cls: 'apr-chip--returned',  icon: <RotateCcw size={11} /> },
    pending:    { label: 'Pending',   cls: 'apr-chip--pending',   icon: <Clock size={11} /> },
    draft:      { label: 'Draft',     cls: 'apr-chip--draft',     icon: null },
    submitted:  { label: 'Submitted', cls: 'apr-chip--submitted', icon: <Clock size={11} /> },
    reviewed:   { label: 'Reviewed',  cls: 'apr-chip--reviewed',  icon: <Star size={11} /> },
    finalized:  { label: 'Finalized', cls: 'apr-chip--finalized', icon: <CheckCircle size={11} /> },
  };
  const cfg = map[status] || map['pending'];
  return (
    <span className={`apr-chip ${cfg.cls}`}>
      {cfg.icon}
      {cfg.label}
    </span>
  );
};

// ─── Workflow Stepper ─────────────────────────────────────────────────────────
const STEPS = [
  { key: APPRAISAL_STATUS_CODES.DRAFT,     label: 'Draft',     num: 1 },
  { key: APPRAISAL_STATUS_CODES.SUBMITTED, label: 'Submitted', num: 2 },
  { key: APPRAISAL_STATUS_CODES.REVIEWED,  label: 'Reviewed',  num: 3 },
  { key: APPRAISAL_STATUS_CODES.FINALIZED, label: 'Finalized', num: 4 },
];

const STATUS_HINTS = {
  [APPRAISAL_STATUS_CODES.DRAFT]:      'You can edit and save as draft or submit for review.',
  [APPRAISAL_STATUS_CODES.SUBMITTED]:  'Awaiting manager review. Editing is disabled.',
  [APPRAISAL_STATUS_CODES.RETURNED]:   'Manager requested revisions. Please update and resubmit.',
  [APPRAISAL_STATUS_CODES.REVIEWED]:   'Manager has reviewed. Ready for finalization.',
  [APPRAISAL_STATUS_CODES.FINALIZED]:  'Appraisal is complete and locked.',
};

const WorkflowStepper = ({ currentStatus }) => {
  const stepOrder = [
    APPRAISAL_STATUS_CODES.DRAFT,
    APPRAISAL_STATUS_CODES.SUBMITTED,
    APPRAISAL_STATUS_CODES.REVIEWED,
    APPRAISAL_STATUS_CODES.FINALIZED,
  ];
  const isReturned = currentStatus === APPRAISAL_STATUS_CODES.RETURNED;
  const effectiveStatus = isReturned ? APPRAISAL_STATUS_CODES.DRAFT : currentStatus;
  const currentIdx = stepOrder.indexOf(effectiveStatus);

  return (
    <div className="apr-stepper">
      {STEPS.map((step, idx) => {
        const isDone    = !isReturned && idx < currentIdx;
        const isActive  = step.key === effectiveStatus;
        const isReturnedStep = isReturned && idx === 0;

        let circleClass = '';
        let labelClass  = '';
        if (isDone)           { circleClass = 'apr-step-circle--done';     labelClass = 'apr-step-label--done';     }
        else if (isReturnedStep){ circleClass = 'apr-step-circle--returned'; labelClass = 'apr-step-label--returned'; }
        else if (isActive)    { circleClass = 'apr-step-circle--active';   labelClass = 'apr-step-label--active';   }

        return (
          <React.Fragment key={step.key}>
            <div className="apr-step">
              <div className={`apr-step-circle ${circleClass}`}>
                {isDone ? <CheckCircle size={14} /> : isReturnedStep ? <RotateCcw size={13} /> : step.num}
              </div>
              <span className={`apr-step-label ${labelClass}`}>
                {isReturnedStep ? 'Returned' : step.label}
              </span>
            </div>
            {idx < STEPS.length - 1 && (
              <div className={`apr-step-connector ${isDone ? 'apr-step-connector--active' : ''}`} />
            )}
          </React.Fragment>
        );
      })}
      <div className="apr-status-hint">
        {STATUS_HINTS[currentStatus]}
      </div>
    </div>
  );
};

// ─── Score Row ────────────────────────────────────────────────────────────────
const ScoreRow = ({ label, hint, value, onChange, weight = '50', disabled = false }) => (
  <div className="apr-score-row">
    <div className="apr-score-cell-main">
      <div className="apr-score-label">{label}</div>
      {hint && <div className="apr-score-hint">{hint}</div>}
    </div>
    <div className="apr-score-cell-rating">
      <InputField
        disabled={disabled}
        type="number"
        value={String(value ?? '')}
        onChange={({ value }) => onChange(value)}
        min="0"
        max="5"
        step="0.1"
        inputWidth="100%"
      />
    </div>
    <div className="apr-score-cell-weight">{weight}%</div>
    <div className="apr-score-cell-result" />
  </div>
);

// ─── Objective Row ────────────────────────────────────────────────────────────
const ObjectiveRow = React.memo(({
  obj,
  isExpanded,
  onToggle,
  savingObjectiveId,
  onRatingChange,
  onCommentsChange,
  disabled = false
}) => {
  const selfRating =
    obj.dataValues?.find(dv => dv.dataElement === OBJECTIVE_APPRAISAL_DE.SELF_RATING)?.value || '';

  const selfComments =
    obj.dataValues?.find(dv => dv.dataElement === OBJECTIVE_APPRAISAL_DE.SELF_COMMENTS)?.value || '';

  return (
    <div>
      {/* MAIN ROW */}
      <div
        className={`apr-obj-row${isExpanded ? ' apr-obj-row--expanded' : ''}`}
        onClick={onToggle}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="apr-obj-name">
            <span
              className={`apr-obj-expand-icon${isExpanded ? ' apr-obj-expand-icon--open' : ''}`}
            >
              <ChevronRight size={15} />
            </span>
            {obj.name}
          </div>
          {obj.description && (
            <div className="apr-obj-desc">
              {obj.description.length > 90
                ? `${obj.description.substring(0, 90)}…`
                : obj.description}
            </div>
          )}
        </div>

        <div style={{ width: 160, flexShrink: 0 }}>
          <ProgressBar value={obj.progress} />
        </div>

        <div style={{ width: 110, flexShrink: 0, textAlign: 'center', fontSize: '0.8rem', color: 'var(--apr-text-secondary)' }}>
          {obj.completedTasks}/{obj.taskCount} done
        </div>

        <div style={{ width: 105, flexShrink: 0, textAlign: 'center' }}>
          <StatusChip status={obj.approvalStatus || 'pending'} />
        </div>

        <div style={{ width: 105, flexShrink: 0, textAlign: 'center', fontSize: '0.8rem', color: 'var(--apr-text-muted)' }}>
          {obj.completionDate || '—'}
        </div>
      </div>

      {/* EXPANDED SECTION */}
      {isExpanded && (
        <div className="apr-obj-panel">
          <h5 className="apr-obj-panel-title">
            <Star size={14} />
            Rate This Objective
          </h5>

          <div className="apr-rating-row">
            <span className="apr-rating-label">Overall Rating (0 – 5):</span>
            <div style={{ width: 100 }}>
              <InputField
                disabled={disabled}
                type="number"
                value={selfRating}
                onChange={({ value }) => {
                  if (value === '' || /^\d*\.?\d*$/.test(value)) {
                    onRatingChange(value);
                  }
                }}
                min="0"
                max="5"
                step="0.1"
                inputWidth="100%"
              />
            </div>
            <span className="apr-rating-hint">Task Objectives (SMART)</span>
          </div>

          <TextAreaField
            label="Comments"
            value={selfComments}
            rows={2}
            inputWidth="100%"
            disabled={disabled}
            onChange={({ value }) => onCommentsChange(value)}
          />

          {savingObjectiveId === obj.id && (
            <div className="apr-saving-indicator">
              <CircularLoader small />
              Saving…
            </div>
          )}
        </div>
      )}

      {/* No tasks message */}
      {isExpanded && (!obj.tasks || obj.tasks.length === 0) && (
        <div className="apr-no-tasks">
          No tasks recorded for this objective.
        </div>
      )}
    </div>
  );
});

// ─── Main Component ───────────────────────────────────────────────────────────
const AppraisalForm = ({
  positionTEI,
  programStage,
  orgUnit,
}) => {

   const normalizeTei = (input) => {
        if (!input) return '';
        if (typeof input === 'string') return input.trim();
        if (typeof input === 'object' && input !== null) {
          return input.tei || input.trackedEntityInstance || input.id || input.value || '';
        }
        return String(input).trim();
      };

   const positionTeiString = normalizeTei(positionTEI);
      
  const engine = useDataEngine();
  const { user } = useAuth();

  const [appraisalData, setAppraisalData] = useState({
    employeeSelfRatingTask: '',
    employeeSelfRatingBehavioural: '',
    employeeSelfComments: '',
    managerRatingTask: '',
    managerRatingBehavioural: '',
    managerComments: '',
    appraisalStatus: 'Draft',
    eventId: null,
  });

  const [recentObjectives, setRecentObjectives] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedObjectiveId, setExpandedObjectiveId] = useState(null);
  const [savingObjectiveId, setSavingObjectiveId] = useState(null);
  const [isEmployee, setIsEmployee] = useState(false);
  const [isManager, setIsManager] = useState(false);
  const [roleCheckComplete, setRoleCheckComplete] = useState(false);
  const [incumbentTei, setIncumbentTei] = useState(null);
  const [managerCanView, setManagerCanView] = useState(false);

  const saveObjectiveRating = async (objectiveId, dataElement, value) => {
    setSavingObjectiveId(objectiveId);
    try {
      const objective = recentObjectivesRef.current.find(obj => obj.id === objectiveId);
      if (!objective) throw new Error('Objective not found');
      const updatedDataValues = [...(objective.dataValues || [])];
      const idx = updatedDataValues.findIndex(dv => dv.dataElement === dataElement);
      if (idx >= 0) {
        updatedDataValues[idx] = { dataElement, value };
      } else {
        updatedDataValues.push({ dataElement, value });
      }

      const fullEventResponse = await engine.query({
        event: {
          resource: `events/${objectiveId}`,
          params: { fields: 'event,program,programStage,orgUnit,eventDate,status,dataValues[dataElement,value]' }
        }
      });

      if (!fullEventResponse.event) {
        throw new Error(`Objective event ${objectiveId} not found`);
      }

      const fullEvent = fullEventResponse.event;
      if (!fullEvent) throw new Error('Objective event not found');

      await engine.mutate({
        type: 'update',
        resource: `events/${objectiveId}`,
        data: {
          ...fullEvent,
          dataValues: updatedDataValues,
          program: 'yiT00XKrrHP',
          programStage: 'VIBtpvkpHFR',
          status: 'ACTIVE'
        },
        headers: { 'Content-Type': 'application/json' }
      });

      setRecentObjectives(prev =>
        prev.map(obj =>
          obj.id === objectiveId
            ? { ...obj, dataValues: updatedDataValues }
            : obj
        )
      );

    } catch (err) {
      console.error('Save rating failed:', err);
      alert(`Failed to save rating: ${err.message}`);
    } finally {
      setSavingObjectiveId(null);
    }
  };

   useEffect(() => {
      if (process.env.NODE_ENV === 'development') {
        console.log('🔍 AppraisalData Debug:', {
          positionTeiString,
          eventId: appraisalData.eventId,
          status: appraisalData.appraisalStatus,
          employeeRatings: {
            task: appraisalData.employeeSelfRatingTask,
            behavioural: appraisalData.employeeSelfRatingBehavioural,
            comments: appraisalData.employeeSelfComments
          },
          managerRatings: {
            task: appraisalData.managerRatingTask,
            behavioural: appraisalData.managerRatingBehavioural,
            comments: appraisalData.managerComments
          }
        });
      }
   }, [positionTeiString, appraisalData]);

   useEffect(() => {
      recentObjectivesRef.current = recentObjectives;
    }, [recentObjectives]);
    
    useEffect(() => {
      return () => {
        Object.values(debounceTimeouts.current).forEach(timeout => {
          clearTimeout(timeout);
        });
        debounceTimeouts.current = {};
      };
   }, []);


  const calculateObjectiveProgress = (tasks = []) => {
    if (!tasks || tasks.length === 0) return 0;
    const total = tasks.reduce((s, t) => s + (t.progressPercent || 0), 0);
    return Math.round(total / tasks.length);
  };

  const extractAttributes = (attributes = []) => {
    const map = {};
    attributes.forEach((attr) => (map[attr.attribute] = attr.value));
    return map;
  };

  const getIncumbentId = (position) => {
    if (!position || !position.attributes) return null;
    const attrs = extractAttributes(position.attributes);
    return attrs[POSITION_ATTRIBUTES.CURRENT_INCUMBENT] || null;
  };

  const debounceTimeouts = useRef({});

  const debounceSave = useCallback((key, fn, delay = 500) => {
    if (debounceTimeouts.current[key]) {
      clearTimeout(debounceTimeouts.current[key]);
    }
    debounceTimeouts.current[key] = setTimeout(() => {
      fn();
      delete debounceTimeouts.current[key];
    }, delay);
  }, []);

    useEffect(() => {
      if (!positionTeiString || !user?.id) {
        setIsEmployee(false);
        setIsManager(false);
        setRoleCheckComplete(true);
        return;
      }

      let cancelled = false;

      const resolveRoles = async () => {
        try {
          const positionRes = await engine.query({
            position: {
              resource: `trackedEntityInstances/${positionTeiString}`,
              params: { fields: 'attributes[attribute,value]' }
            }
          });
          
          const posAttrs = extractAttributes(positionRes.position?.attributes || []);
          const incumbentIdentifier = posAttrs[POSITION_ATTRIBUTES.CURRENT_INCUMBENT];
          
          if (!incumbentIdentifier) {
            console.warn('No incumbent identifier found for position');
            if (!cancelled) {
              setIsEmployee(false);
              setIsManager(false);
              setRoleCheckComplete(true);
            }
            return;
          }

          const isEmp = user?.id === incumbentIdentifier || user?.username === incumbentIdentifier;

            let isMgr = false;
            try {
              const posAttrs = extractAttributes(positionRes.position?.attributes || []);
              const managerPositionTei = posAttrs[POSITION_ATTRIBUTES.REPORTING_TO];
              
              if (managerPositionTei) {
                const mgrPosRes = await engine.query({
                  mgrPosition: {
                    resource: `trackedEntityInstances/${managerPositionTei}`,
                    params: { fields: 'attributes[attribute,value]' }
                  }
                });
                const mgrAttrs = extractAttributes(mgrPosRes.mgrPosition?.attributes || []);
                const mgrUserIdentifier = mgrAttrs[POSITION_ATTRIBUTES.CURRENT_INCUMBENT];
                isMgr = user?.id === mgrUserIdentifier || user?.username === mgrUserIdentifier;
              }
            } catch (err) {
              console.warn('Failed to check manager role:', err);
            }

          if (!cancelled) {
            setIsEmployee(isEmp);
            setIsManager(isMgr);
            setRoleCheckComplete(true);
            console.log('🔐 Roles resolved:', { 
              userId: user?.id, 
              incumbentIdentifier, 
              isEmployee: isEmp, 
              isManager: isMgr 
            });
          }
          
        } catch (err) {
          console.error('Role resolution failed:', err);
          if (!cancelled) {
            setIsEmployee(user?.id === positionTeiString);
            setIsManager(false);
            setRoleCheckComplete(true);
          }
        }
      };

      resolveRoles();
      return () => { cancelled = true; };
    }, [positionTeiString, user?.id, user?.username, engine]);
  

  const canEmployeeEdit = ['draft', 'returned'].includes(appraisalData.appraisalStatus) && isEmployee;
  const canManagerEdit = ['submitted', 'reviewed'].includes(appraisalData.appraisalStatus) && (isManager || managerCanView);

  const getSixMonthsAgo = () => {
    const date = new Date();
    date.setMonth(date.getMonth() - 6);
    return date.toISOString().split('T')[0];
  };

  const safeParse = (str) => {
    const num = parseFloat(str);
    return isNaN(num) ? 0 : num;
  };

  const calculateTotalScore = (taskRating, behaviouralRating) => {
    const task = safeParse(taskRating);
    const behavioural = safeParse(behaviouralRating);
    return ((task * 0.5) + (behavioural * 0.5)).toFixed(2);
  };

  const recentObjectivesRef = useRef([]);

  useEffect(() => {
    recentObjectivesRef.current = recentObjectives;
  }, [recentObjectives]);

  useEffect(() => {
    return () => {
      Object.values(debounceTimeouts.current).forEach(timeout => {
        clearTimeout(timeout);
      });
      debounceTimeouts.current = {};
    };
  }, []);

  useEffect(() => {
    if (!positionTeiString || !programStage || !orgUnit) return;
    
    const loadAppraisal = async () => {
      try {
        const response = await engine.query({
          appraisal: {
            resource: 'events',
            params: {
              program: 'yiT00XKrrHP',
              programStage,
              orgUnit,
              ouMode: 'ACCESSIBLE',
              trackedEntityInstance: positionTeiString,
              fields: 'event,dataValues[dataElement,value],status',
              pageSize: 1,
            },
          },
        });
        
       const event = response.appraisal?.events?.[0];
        if (event) {
          const dv = (de) => event.dataValues?.find(d => d.dataElement === de)?.value || '';
          const rawStatus = dv(DATA_ELEMENTS.APPRAISAL_STATUS);
          
          setAppraisalData({
            employeeSelfRatingTask: dv(DATA_ELEMENTS.EMPLOYEE_SELF_RATING_TASK),
            employeeSelfRatingBehavioural: dv(DATA_ELEMENTS.EMPLOYEE_SELF_RATING_BEHAVIOURAL),
            employeeSelfComments: dv(DATA_ELEMENTS.EMPLOYEE_COMMENTS),
            managerRatingTask: dv(DATA_ELEMENTS.MANAGER_RATING_TASK),
            managerRatingBehavioural: dv(DATA_ELEMENTS.MANAGER_RATING_BEHAVIOURAL),
            managerComments: dv(DATA_ELEMENTS.MANAGER_COMMENTS),
            appraisalStatus: rawStatus ? rawStatus.toLowerCase() : APPRAISAL_STATUS_CODES.DRAFT,
            eventId: event.event,
          });
        } else {
          console.log('ℹ️ No appraisal event found for', {
            positionTeiString,
            programStage,
            orgUnit
          });
        }
      } catch (err) {
        console.error('Failed to load appraisal:', err);
      }
    };
    
    loadAppraisal();
  }, [positionTeiString, programStage, orgUnit, engine]);


  useEffect(() => {
    if (!positionTeiString || !orgUnit) return;
    let cancelled = false;

    const loadRecentObjectives = async () => {
      try {
        const sixMonthsAgo = getSixMonthsAgo();

        const objectivesResponse = await engine.query({
          objectives: {
            resource: 'events',
            params: {
              program: 'yiT00XKrrHP',
              programStage: 'VIBtpvkpHFR',
              eventStartDate: sixMonthsAgo,
              orgUnit: orgUnit,
              ouMode: 'ACCESSIBLE',
              fields: 'event,trackedEntityInstance,orgUnit,eventDate,dataValues[dataElement,value]',
              pageSize: 50,
            },
          },
        });

        const allObjs = (objectivesResponse.objectives?.events || []).map((event) => ({
          id: event.event,
          trackedEntityInstance: event.trackedEntityInstance,
          name: event.dataValues?.find((dv) => dv.dataElement === OBJECTIVE_DATA_ELEMENTS.NAME)?.value || 'Unnamed Objective',
          description: event.dataValues?.find((dv) => dv.dataElement === OBJECTIVE_DATA_ELEMENTS.DESCRIPTION)?.value || '',
          startDate: event.dataValues?.find((dv) => dv.dataElement === OBJECTIVE_DATA_ELEMENTS.START_DATE)?.value || '',
          completionDate: event.dataValues?.find((dv) => dv.dataElement === OBJECTIVE_DATA_ELEMENTS.COMPLETION_DATE)?.value || '',
          approvalStatus: event.dataValues?.find((dv) => dv.dataElement === OBJECTIVE_DATA_ELEMENTS.APPROVAL_STATUS)?.value || 'pending',
          positionTei: event.dataValues?.find(dv => dv.dataElement === 'SlgO60cMK3F')?.value,
          eventDate: event.eventDate,
          dataValues: event.dataValues || [],
        }));

        console.log(`Found ${allObjs.length} total objectives for position ${positionTeiString}`);

        const tasksResponse = await engine.query({
          allTasks: {
            resource: 'events',
            params: {
              program: 'yiT00XKrrHP',
              programStage: 'Nv9nYNcBTdM',
              fields: 'event,dataValues[dataElement,value]',
              pageSize: 100,
            },
          },
        });

        const allTasks = tasksResponse.allTasks?.events || [];
        console.log(`Found ${allTasks.length} total tasks`);

        const tasksByObjective = {};

        allTasks.forEach(event => {
          const dataValues = event.dataValues || [];
          const objectiveId = dataValues.find(dv => dv.dataElement === 'plJovUlxfQ0')?.value;
          const taskPositionTei = dataValues.find(dv => dv.dataElement === 'SlgO60cMK3F')?.value;

          const objective = allObjs.find(obj => obj.id === objectiveId);
          const isRelevant =
            taskPositionTei === positionTeiString ||
            (objective && objective.positionTei === positionTeiString);

          if (objectiveId && isRelevant) {
            if (!tasksByObjective[objectiveId]) {
              tasksByObjective[objectiveId] = [];
            }
            tasksByObjective[objectiveId].push({
              id: event.event,
              name: dataValues.find(dv => dv.dataElement === 'I0QtZkMbzxN')?.value || 'Unnamed Task',
              progressPercent: parseInt(dataValues.find(dv => dv.dataElement === 'AElpNxzTdRh')?.value) || 0,
              status: dataValues.find(dv => dv.dataElement === 'LuTwfuN34pc')?.value || '',
              completionDate: dataValues.find(dv => dv.dataElement === 'J0rjxrDKn1l')?.value || '',
            });
          }
        });

        const objectivesForPosition = allObjs.filter(obj => {
          const directMatch = obj.trackedEntityInstance === positionTeiString;
          const dataElementMatch = obj.positionTei === positionTeiString;
          return directMatch || dataElementMatch;
        });

        console.log(`Objectives for position ${positionTeiString}: ${objectivesForPosition.length}`);

        const objectivesWithProgress = objectivesForPosition.map(obj => {
          const objTasks = tasksByObjective[obj.id] || [];
          const progress = calculateObjectiveProgress(objTasks);
          return {
            ...obj,
            tasks: objTasks,
            progress,
            taskCount: objTasks.length,
            completedTasks: objTasks.filter(t => t.progressPercent === 100).length,
          };
        });

        objectivesWithProgress.sort((a, b) => new Date(b.eventDate) - new Date(a.eventDate));

        if (!cancelled) {
          setRecentObjectives(objectivesWithProgress);
          console.log(`Set ${objectivesWithProgress.length} objectives...`);
          objectivesWithProgress.forEach(obj => {
            console.log(`Objective: "${obj.name}" - Tasks: ${obj.taskCount}`);
          });
        }
      } catch (err) {
        console.error('Failed to load recent objectives:', err);
      }
    };

    loadRecentObjectives();
    return () => { cancelled = true; };
  }, [positionTeiString, orgUnit, engine]);

  const saveAppraisal = async (status) => {
    try {
      setLoading(true);

      const dataValues = [
        { dataElement: DATA_ELEMENTS.EMPLOYEE_SELF_RATING_TASK, value: appraisalData.employeeSelfRatingTask || '' },
        { dataElement: DATA_ELEMENTS.EMPLOYEE_SELF_RATING_BEHAVIOURAL, value: appraisalData.employeeSelfRatingBehavioural || '' },
        { dataElement: DATA_ELEMENTS.EMPLOYEE_COMMENTS, value: appraisalData.employeeSelfComments || '' },
        { dataElement: DATA_ELEMENTS.MANAGER_RATING_TASK, value: appraisalData.managerRatingTask || '' },
        { dataElement: DATA_ELEMENTS.MANAGER_RATING_BEHAVIOURAL, value: appraisalData.managerRatingBehavioural || '' },
        { dataElement: DATA_ELEMENTS.MANAGER_COMMENTS, value: appraisalData.managerComments || '' },
        { dataElement: DATA_ELEMENTS.APPRAISAL_STATUS, value: status },
      ];

      const eventData = {
        program: 'yiT00XKrrHP',
        programStage,
        orgUnit,
        trackedEntityInstance: positionTeiString,
        eventDate: new Date().toISOString().split('T')[0],
        status: 'ACTIVE',
        dataValues,
      };

     if (!appraisalData.eventId) {
            const checkResponse = await engine.query({
              check: {
                resource: 'events',
                params: {
                  program: 'yiT00XKrrHP',
                  programStage,
                  orgUnit,
                  trackedEntityInstance: positionTeiString,
                  fields: 'event',
                  pageSize: 1
                }
              }
            });
            
            const existingEvent = checkResponse.check?.events?.[0]?.event;
            if (existingEvent) {
              appraisalData.eventId = existingEvent;
            }
        }

        console.log('📤 Appraisal Save Payload:', {
            method: appraisalData.eventId ? 'PUT' : 'POST',
            url: appraisalData.eventId 
              ? `/api/events/${appraisalData.eventId}` 
              : '/api/events',
            eventData: {
              program: 'yiT00XKrrHP',
              programStage,
              orgUnit,
              trackedEntityInstance: positionTeiString,
              eventDate: new Date().toISOString().split('T')[0],
              status: 'ACTIVE',
              dataValues: dataValues.map(dv => ({
                dataElement: dv.dataElement,
                value: dv.value,
                preview: typeof dv.value === 'string' && dv.value.length > 50 
                  ? dv.value.substring(0, 50) + '...' 
                  : dv.value
              }))
            }
        });

      if (appraisalData.eventId) {
        await engine.mutate({
          resource: `events/${appraisalData.eventId}`,
          type: 'update',
          data: eventData,
          headers: { 'Content-Type': 'application/json' },
        });
      } else {
        const result = await engine.mutate({
          resource: 'events',
          type: 'create',
          data: eventData,
          headers: { 'Content-Type': 'application/json' },
        });
        setAppraisalData(prev => ({ ...prev, eventId: result.response?.reference || prev.eventId }));
      }

      setAppraisalData(prev => ({ ...prev, appraisalStatus: status }));
      alert('Appraisal saved successfully!');
      } catch (err) {
          console.error('💥 Save error - Full details:', {
            message: err?.message,
            name: err?.name,
            stack: err?.stack,
            response: {
              status: err?.response?.status,
              statusText: err?.response?.statusText,
              url: err?.response?.url,
              body: err?.response?.body,
              text: err?.response?.text,
              json: err?.response?.json,
              conflicts: err?.response?.conflicts,
              importSummaries: err?.response?.importSummaries,
              details: err?.response?.details
            },
            rawError: String(err)
          });
          
          let errorMsg = 'Failed to save appraisal';
          if (err?.response?.body?.message) {
            errorMsg = err.response.body.message;
          } else if (err?.response?.details?.[0]?.message) {
            errorMsg = err.response.details[0].message;
          } else if (err?.message?.includes('import summary')) {
            errorMsg = 'Validation error - check required fields';
          }
          
          setError(`Save failed: ${errorMsg}`);
          alert(`❌ ${errorMsg}\n\nCheck console for full error details.`);
        } finally {
          setLoading(false);
        }
    };

  const computed = useMemo(() => ({
    employeeTotal: ((safeParse(appraisalData.employeeSelfRatingTask) + safeParse(appraisalData.employeeSelfRatingBehavioural)) / 2).toFixed(2),
    managerTotal: ((safeParse(appraisalData.managerRatingTask) + safeParse(appraisalData.managerRatingBehavioural)) / 2).toFixed(2),
  }), [appraisalData]);

  if (loading) {
    return (
      <div className="apr-loading">
        <CircularLoader small />
        <span>Saving appraisal…</span>
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ padding: '1rem' }}>
        <NoticeBox error title="Error">{error}</NoticeBox>
      </div>
    );
  }

  const isMidYear = programStage === 'xbEyVHbXlGK';

   if (!roleCheckComplete) {
      return (
        <div className="apr-loading">
          <CircularLoader small />
          <span>Checking permissions…</span>
        </div>
      );
   }

  return (
    <div className="apr-page">
      {/* ── Page Header ─────────────────────────────────────── */}
      <div className="apr-page-header">
        <div>
          <h4>
            {isMidYear ? 'Mid-Year' : 'Year-End'} Performance Review
          </h4>
          <div className="apr-page-subtitle">Performance Evaluation Form</div>
        </div>
        <span className={`apr-type-badge ${isMidYear ? 'apr-type-badge--mid' : 'apr-type-badge--year'}`}>
          {isMidYear ? '🗓 Mid-Year' : '📅 Year-End'}
        </span>
      </div>

      {/* ── Workflow Stepper ─────────────────────────────────── */}
      <WorkflowStepper currentStatus={appraisalData.appraisalStatus} />

      {/* ── Recent Objectives ────────────────────────────────── */}
      <div className="apr-section">
        <div className="apr-card">
          <div className="apr-card-header">
            <div className="apr-card-header-left">
              <span className="apr-card-icon"><TrendingUp size={17} /></span>
              <h4 className="apr-card-title">Objective Progress — Last 6 Months</h4>
              {recentObjectives.length > 0 && (
                <span className="apr-count-badge">{recentObjectives.length}</span>
              )}
            </div>
          </div>

          <div className="apr-card-body">
            {recentObjectives.length > 0 ? (
              <>
                {/* Column Headers */}
                <div className="apr-obj-header">
                  <div style={{ flex: 1 }}>Objective</div>
                  <div style={{ width: 160 }}>Task Progress</div>
                  <div style={{ width: 110, textAlign: 'center' }}>Tasks</div>
                  <div style={{ width: 105, textAlign: 'center' }}>Status</div>
                  <div style={{ width: 105, textAlign: 'center' }}>Completed</div>
                </div>

                {/* Objective Rows */}
                {recentObjectives.map(obj => {
                  const handleRatingChange = (value) => {
                    if (value === '') {
                      setRecentObjectives(prev =>
                        prev.map(o =>
                          o.id === obj.id
                            ? {
                              ...o,
                              dataValues: [
                                ...(o.dataValues?.filter(dv => dv.dataElement !== OBJECTIVE_APPRAISAL_DE.SELF_RATING) || []),
                                { dataElement: OBJECTIVE_APPRAISAL_DE.SELF_RATING, value: '' }
                              ]
                            }
                            : o
                        )
                      );
                      return;
                    }

                    let numValue = parseFloat(value);
                    if (isNaN(numValue)) return;
                    numValue = Math.max(0, Math.min(5, numValue));
                    const clampedValue = parseFloat(numValue.toFixed(1));

                    setRecentObjectives(prev =>
                      prev.map(o =>
                        o.id === obj.id
                          ? {
                            ...o,
                            dataValues: [
                              ...(o.dataValues?.filter(dv => dv.dataElement !== OBJECTIVE_APPRAISAL_DE.SELF_RATING) || []),
                              { dataElement: OBJECTIVE_APPRAISAL_DE.SELF_RATING, value: String(clampedValue) }
                            ]
                          }
                          : o
                      )
                    );

                    debounceSave(
                      `${obj.id}-rating`,
                      () => saveObjectiveRating(obj.id, OBJECTIVE_APPRAISAL_DE.SELF_RATING, String(clampedValue))
                    );
                  };

                  const handleCommentsChange = (value) => {
                    setRecentObjectives(prev =>
                      prev.map(o =>
                        o.id === obj.id
                          ? {
                            ...o,
                            dataValues: [
                              ...(o.dataValues?.filter(dv => dv.dataElement !== OBJECTIVE_APPRAISAL_DE.SELF_COMMENTS) || []),
                              { dataElement: OBJECTIVE_APPRAISAL_DE.SELF_COMMENTS, value }
                            ]
                          }
                          : o
                      )
                    );
                    debounceSave(
                      `${obj.id}-comments`,
                      () => saveObjectiveRating(obj.id, OBJECTIVE_APPRAISAL_DE.SELF_COMMENTS, value)
                    );
                  };

                  return (
                    <ObjectiveRow
                      key={obj.id}
                      obj={obj}
                      isExpanded={expandedObjectiveId === obj.id}
                      onToggle={() => setExpandedObjectiveId(
                        expandedObjectiveId === obj.id ? null : obj.id
                      )}
                      savingObjectiveId={savingObjectiveId}
                      onRatingChange={handleRatingChange}
                      onCommentsChange={handleCommentsChange}
                      disabled={!canEmployeeEdit}
                    />
                  );
                })}
              </>
            ) : (
              <div className="apr-no-objectives">
                <div className="apr-no-objectives-icon"><TrendingUp size={36} /></div>
                <div className="apr-no-objectives-text">No objectives found for the last 6 months.</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Employee Self-Appraisal ──────────────────────────── */}
      {(isEmployee || (isManager && appraisalData.appraisalStatus !== APPRAISAL_STATUS_CODES.DRAFT)) && (
        <div className="apr-section">
          <div className="apr-card">
            <div className="apr-card-header">
              <div className="apr-card-header-left">
                <span className="apr-card-icon"><Star size={17} /></span>
                <h4 className="apr-card-title">
                  {isEmployee ? "Employee's Overall Self-Appraisal" : "Employee's Self-Appraisal (Review)"}
                </h4>
              </div>
              {isEmployee && <StatusChip status={appraisalData.appraisalStatus} />}
            </div>

            <div>
              {/* Table Header */}
              <div className="apr-score-header-row">
                <div className="apr-score-cell-main">Criteria</div>
                <div className="apr-score-cell-rating" style={{ textAlign: 'center' }}>Rating (0–5)</div>
                <div className="apr-score-cell-weight">Weight</div>
                <div className="apr-score-cell-result">Score</div>
              </div>

              {/* Task Objectives Row */}
              <ScoreRow
                label="Task Objectives"
                hint="SMART Objectives"
                value={appraisalData.employeeSelfRatingTask}
                onChange={(value) => {
                  if (value === '') { setAppraisalData(prev => ({ ...prev, employeeSelfRatingTask: '' })); return; }
                  let num = parseFloat(value);
                  if (isNaN(num)) return;
                  num = Math.max(0, Math.min(5, num));
                  setAppraisalData(prev => ({ ...prev, employeeSelfRatingTask: String(parseFloat(num.toFixed(1))) }));
                }}
                weight={50}
                disabled={!canEmployeeEdit}
              />

              {/* Behavioural Row */}
              <ScoreRow
                label="Behavioural Criteria"
                hint="Inclusive of Values"
                value={appraisalData.employeeSelfRatingBehavioural}
                onChange={(value) => {
                  if (value === '') { setAppraisalData(prev => ({ ...prev, employeeSelfRatingBehavioural: '' })); return; }
                  let num = parseFloat(value);
                  if (isNaN(num)) return;
                  num = Math.max(0, Math.min(5, num));
                  setAppraisalData(prev => ({ ...prev, employeeSelfRatingBehavioural: String(parseFloat(num.toFixed(1))) }));
                }}
                weight={50}
                disabled={!canEmployeeEdit}
              />

              {/* Total Row */}
              <div className="apr-score-total-row">
                <div className="apr-score-cell-main" style={{ fontWeight: 700 }}>Total</div>
                <div className="apr-score-cell-rating" style={{ textAlign: 'center', fontWeight: 700 }}>
                  {computed.employeeTotal}
                </div>
                <div className="apr-score-cell-weight" style={{ fontWeight: 700 }}>100%</div>
                <div className="apr-score-cell-result">
                  {calculateTotalScore(appraisalData.employeeSelfRatingTask, appraisalData.employeeSelfRatingBehavioural)}
                </div>
              </div>
            </div>

            {/* Comments */}
            <div className="apr-comments-section">
              <TextAreaField
                label="Comments"
                value={appraisalData.employeeSelfComments}
                onChange={({ value }) => setAppraisalData(prev => ({ ...prev, employeeSelfComments: value }))}
                rows={4}
                inputWidth="100%"
                disabled={!canEmployeeEdit}
              />
            </div>

            {/* Actions */}
            {isEmployee && (
              <div className="apr-actions-bar">
                <button
                 style={{ 
                  boxShadow: "0 8px 10px rgba(108, 105, 105, 0.5)",
                  color: 'white',
                  backgroundColor: 'rgb(228, 138, 3)',
                  borderRadius: '8px',
                  border: 'none',
                  width: '90px',
                  height: '35px',
                  cursor: 'pointer' }}
                 onClick={() => saveAppraisal(APPRAISAL_STATUS_CODES.DRAFT)}>
                  Save Draft
                </button>
                <button
                  style={{ 
                    boxShadow: "0 8px 10px rgba(108, 105, 105, 0.5)",
                    color: 'white',
                    backgroundColor: 'rgb(5, 5, 210)',
                    borderRadius: '8px',
                    border: 'none',
                    width: '150px',
                    height: '35px',
                    cursor: 'pointer' }}
                  onClick={() => saveAppraisal(APPRAISAL_STATUS_CODES.SUBMITTED)}>
                  Submit for Review
                </button>
              </div>
            )}

            {isManager && appraisalData.appraisalStatus === APPRAISAL_STATUS_CODES.SUBMITTED && (
              <div style={{ padding: '0.75rem 1rem' }}>
                <NoticeBox info>
                  Review the employee's self-appraisal above, then provide your evaluation below.
                </NoticeBox>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── No Appraisal Yet (Manager View) ─────────────────── */}
      {isManager && !appraisalData.eventId && (
        <div style={{ marginBottom: '1rem' }}>
          <NoticeBox warning title="No appraisal submitted yet">
            This employee has not submitted an appraisal for the {isMidYear ? 'Mid-Year' : 'Year-End'} review period.
            Please ask them to complete and submit their self-appraisal first.
            <div style={{ marginTop: '0.75rem' }}>
              <Button small secondary onClick={() => window.location.reload()}>
                Refresh
              </Button>
            </div>
          </NoticeBox>
        </div>
      )}

      {/* ── Manager / Evaluator Appraisal ───────────────────── */}
      {(isManager || (isEmployee && appraisalData.appraisalStatus === APPRAISAL_STATUS_CODES.RETURNED)) && appraisalData.eventId && (
        <div className="apr-section">
          <div className="apr-card">
            <div className="apr-card-header">
              <div className="apr-card-header-left">
                <span className="apr-card-icon"><Users size={17} /></span>
                <h4 className="apr-card-title">
                  {isManager ? "Evaluator's Overall Appraisal" : "Manager's Feedback (Returned for Revisions)"}
                </h4>
              </div>
              {isManager && <StatusChip status={appraisalData.appraisalStatus} />}
            </div>

            <div>
              {/* Table Header */}
              <div className="apr-score-header-row">
                <div className="apr-score-cell-main">Criteria</div>
                <div className="apr-score-cell-rating" style={{ textAlign: 'center' }}>Rating (0–5)</div>
                <div className="apr-score-cell-weight">Weight</div>
                <div className="apr-score-cell-result">Score</div>
              </div>

              {/* Task Objectives */}
              <div className="apr-score-row">
                <div className="apr-score-cell-main">
                  <div className="apr-score-label">Task Objectives</div>
                  <div className="apr-score-hint">SMART Objectives</div>
                </div>
                <div className="apr-score-cell-rating">
                  <InputField
                    disabled={!canManagerEdit}
                    type="number"
                    value={String(appraisalData.managerRatingTask ?? '')}
                    onChange={({ value }) => setAppraisalData(prev => ({ ...prev, managerRatingTask: value }))}
                    min="0"
                    max="5"
                    step="0.1"
                    inputWidth="100%"
                  />
                </div>
                <div className="apr-score-cell-weight">50%</div>
                <div className="apr-score-cell-result">
                  {calculateTotalScore(appraisalData.managerRatingTask, '')}
                </div>
              </div>

              {/* Behavioural */}
              <div className="apr-score-row">
                <div className="apr-score-cell-main">
                  <div className="apr-score-label">Behavioural Criteria</div>
                  <div className="apr-score-hint">Inclusive of Values</div>
                </div>
                <div className="apr-score-cell-rating">
                  <InputField
                    disabled={!canManagerEdit}
                    type="number"
                    value={String(appraisalData.managerRatingBehavioural ?? '')}
                    onChange={({ value }) => setAppraisalData(prev => ({ ...prev, managerRatingBehavioural: value }))}
                    min="0"
                    max="5"
                    step="0.1"
                    inputWidth="100%"
                  />
                </div>
                <div className="apr-score-cell-weight">50%</div>
                <div className="apr-score-cell-result">
                  {calculateTotalScore('', appraisalData.managerRatingBehavioural)}
                </div>
              </div>

              {/* Total */}
              <div className="apr-score-total-row">
                <div className="apr-score-cell-main" style={{ fontWeight: 700 }}>Total</div>
                <div className="apr-score-cell-rating" style={{ textAlign: 'center', fontWeight: 700 }}>
                  {computed.managerTotal}
                </div>
                <div className="apr-score-cell-weight" style={{ fontWeight: 700 }}>100%</div>
                <div className="apr-score-cell-result">
                  {calculateTotalScore(appraisalData.managerRatingTask, appraisalData.managerRatingBehavioural)}
                </div>
              </div>
            </div>

            {/* Manager Comments */}
            <div className="apr-comments-section">
              <TextAreaField
                label="Manager's Comments"
                value={appraisalData.managerComments}
                rows={4}
                inputWidth="100%"
                disabled={!canManagerEdit}
                onChange={({ value }) => setAppraisalData(prev => ({ ...prev, managerComments: value }))}
              />
            </div>

             {/* Manager Actions */}
            {isManager && (
              <div className="apr-actions-bar">
                {/* Status = SUBMITTED: Show Return & Review buttons */}
                {appraisalData.appraisalStatus === APPRAISAL_STATUS_CODES.SUBMITTED && (
                  <>
                    <button
                      style={{ 
                        boxShadow: "0 8px 10px rgba(108, 105, 105, 0.5)",
                        color: 'white',
                        backgroundColor: 'rgb(5, 5, 210)',
                        borderRadius: '8px',
                        border: 'none',
                        width: '170px',
                        height: '35px',
                        cursor: 'pointer' }}
                      onClick={() => saveAppraisal(APPRAISAL_STATUS_CODES.RETURNED)}
                      disabled={!appraisalData.managerComments?.trim()}
                    >
                      ↩️ Return with Comments
                    </button>
                    <button
                      style={{ 
                        boxShadow: "0 8px 10px rgba(108, 105, 105, 0.5)",
                        color: 'white',
                        backgroundColor: 'rgb(5, 5, 210)',
                        borderRadius: '8px',
                        border: 'none',
                        width: '50px',
                        height: '35px',
                        cursor: 'pointer' }}
                      onClick={() => saveAppraisal(APPRAISAL_STATUS_CODES.REVIEWED)}
                      disabled={!appraisalData.managerRatingTask || !appraisalData.managerRatingBehavioural}
                    >
                      Review
                    </button>
                  </>
                )}
                
                {/* Status = RETURNED: Show info notice */}
                {appraisalData.appraisalStatus === APPRAISAL_STATUS_CODES.RETURNED && (
                  <NoticeBox info title="Returned to Employee" style={{ marginBottom: 0 }}>
                    This appraisal was returned for revisions. Employee can now edit and resubmit.
                  </NoticeBox>
                )}
                
                {/* Status = REVIEWED: Show Finalize button */}
                {appraisalData.appraisalStatus === APPRAISAL_STATUS_CODES.REVIEWED && (
                  <button
                    style={{ 
                        boxShadow: "0 8px 10px rgba(108, 105, 105, 0.5)",
                        color: 'white',
                        backgroundColor: 'rgb(5, 5, 210)',
                        borderRadius: '8px',
                        border: 'none',
                        width: '150px',
                        height: '35px',
                        cursor: 'pointer' }}
                    onClick={() => saveAppraisal(APPRAISAL_STATUS_CODES.FINALIZED)}
                    disabled={loading}
                  >
                    ✅ Finalize Appraisal
                  </button>
                )}
                
                {/* Status = FINALIZED: Show locked notice */}
                {appraisalData.appraisalStatus === APPRAISAL_STATUS_CODES.FINALIZED && (
                  <NoticeBox success title="Appraisal Finalized">
                    This appraisal has been finalized and is now locked. No further changes can be made.
                  </NoticeBox>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Status Footer ────────────────────────────────────── */}
      <div className="apr-status-footer">
        <span className="apr-status-footer-label">Current Status:</span>
        <StatusChip status={appraisalData.appraisalStatus} />
        {appraisalData.appraisalStatus === APPRAISAL_STATUS_CODES.FINALIZED && (
          <span style={{ color: 'var(--apr-success)', display: 'flex', alignItems: 'center' }}>
            <Lock size={14} style={{ marginRight: 4 }} /> Locked
          </span>
        )}
      </div>
    </div>
  );
};

export default AppraisalForm;
